import { PruneTaskRunsArgs } from './prune-task-runs/PruneTaskRunsArgs'
import { validatePruneTaskRunsArgs } from './prune-task-runs/PruneTaskRunsArgs.utils'
import { UspstfDownloadArgs } from './cpg.uspstf.download/UspstfDownloadArgs'
import { validateUspstfDownloadArgs } from './cpg.uspstf.download/UspstfDownloadArgs.utils'
import { UspstfProcessArgs } from './cpg.uspstf.process/UspstfProcessArgs'
import { validateUspstfProcessArgs } from './cpg.uspstf.process/UspstfProcessArgs.utils'
import { ClassifyArgs } from './cpg.classify/ClassifyArgs'
import { validateClassifyArgs } from './cpg.classify/ClassifyArgs.utils'
import { NotifyArgs } from './cpg.notify/NotifyArgs'
import { validateNotifyArgs } from './cpg.notify/NotifyArgs.utils'
import { ProposalsClusterArgs } from './cpg.proposals.cluster/ProposalsClusterArgs'
import { validateProposalsClusterArgs } from './cpg.proposals.cluster/ProposalsClusterArgs.utils'
import { FindingsSyncArgs } from './findings.sync/FindingsSyncArgs'
import { validateFindingsSyncArgs } from './findings.sync/FindingsSyncArgs.utils'
import { FindingsSyntheticArgs } from './findings.synthetic/FindingsSyntheticArgs'
import { validateFindingsSyntheticArgs } from './findings.synthetic/FindingsSyntheticArgs.utils'
import { WarehouseRefreshArgs } from './warehouse.refresh/WarehouseRefreshArgs'
import { validateWarehouseRefreshArgs } from './warehouse.refresh/WarehouseRefreshArgs.utils'

/**
 * Custom args editors for the Super Admin Tasks page (see docs/tasks/HOWTO_TASK.md,
 * "Custom args editor in the Super Admin UI").
 *
 * A task may register a component that edits AND displays its args. The Run dialog
 * mounts it in edit mode (seeded with the task's scheduled_args); the run details
 * panel mounts it read-only (via TaskArgsView) to render a run's recorded args.
 * Tasks without an entry fall back to a raw JSON editor / a JSON code block.
 */
export interface TaskArgsComponentProps {
  /** Current args value (JSON-serializable; undefined/null = no args). */
  value: unknown
  /** Edit mode only: report every change. Validity is judged by `validate`, not here. */
  onChange?: (value: unknown) => void
  /** Render a non-editable view (used in the run details panel). */
  readOnly?: boolean
}

export interface TaskArgsEntry {
  Component: React.ComponentType<TaskArgsComponentProps>
  /**
   * Validates a candidate args value; return null when ok, else a message. Drives the
   * Run button (checked on mount for the scheduled_args seed and after every change).
   * Server-side argsSchema validation still applies — this is the immediate feedback.
   */
  validate: (value: unknown) => string | null
}

/**
 * slug → args editor. Add an entry per task that outgrows raw JSON. Task-specific
 * components live under a directory named after the task's slug (prune-task-runs/
 * holds the reference implementation).
 */
const TASK_ARGS_COMPONENTS: Record<string, TaskArgsEntry> = {
  'prune-task-runs': { Component: PruneTaskRunsArgs, validate: validatePruneTaskRunsArgs },
  'cpg.uspstf.download': { Component: UspstfDownloadArgs, validate: validateUspstfDownloadArgs },
  'cpg.uspstf.process': { Component: UspstfProcessArgs, validate: validateUspstfProcessArgs },
  'cpg.classify': { Component: ClassifyArgs, validate: validateClassifyArgs },
  'cpg.notify': { Component: NotifyArgs, validate: validateNotifyArgs },
  'cpg.proposals.cluster': {
    Component: ProposalsClusterArgs,
    validate: validateProposalsClusterArgs,
  },
  'findings.sync': { Component: FindingsSyncArgs, validate: validateFindingsSyncArgs },
  'findings.synthetic': {
    Component: FindingsSyntheticArgs,
    validate: validateFindingsSyntheticArgs,
  },
  'warehouse.refresh': { Component: WarehouseRefreshArgs, validate: validateWarehouseRefreshArgs },
}

export function getTaskArgsEntry(slug: string): TaskArgsEntry | undefined {
  return TASK_ARGS_COMPONENTS[slug]
}
